import { Component, esc } from '../../core/component.js';
import { api, ApiError } from '../../core/api.js';
import { t } from '../../core/i18n.js';

export class AdminView extends Component {
  constructor() {
    super();
    this.state = {
      activePanel: 'dashboard',
      panels: ['dashboard', 'users', 'properties', 'contracts', 'inquiries'],
      items: [],
      selectedIndex: 0,
      loading: false,
      error: null,
      searchQuery: '',
      searchBy: 'all',
      detail: null,
      stats: {},
    };
    this._keyHandler = this._handleKey.bind(this);
  }

  template() {
    const { activePanel, panels, items, selectedIndex, loading, error, searchQuery, searchBy, detail, stats } = this.state;
    return `
      <div class="admin-terminal" tabindex="0">
        <div class="admin-header">
          <span class="admin-prompt">❯</span>
          <span class="admin-title">ADMIN//${activePanel.toUpperCase()}</span>
          <span class="admin-hint">[↑↓] navigate [Enter] select [Esc] back [/] search</span>
        </div>
        <div class="admin-toolbar">
          ${panels.map((p, i) => `<span class="admin-tab ${p === activePanel ? 'active' : ''}" data-panel="${esc(p)}">[${i + 1}] ${esc(p)}</span>`).join('')}
        </div>
        ${activePanel === 'dashboard' ? this._dashboardHtml() : ''}
        ${activePanel === 'users' ? `
        <div class="admin-search-bar">
          <span class="search-prompt">$</span>
          <select class="search-select" data-action="search-by">
            <option value="all" ${searchBy === 'all' ? 'selected' : ''}>all</option>
            <option value="email" ${searchBy === 'email' ? 'selected' : ''}>email</option>
            <option value="name" ${searchBy === 'name' ? 'selected' : ''}>name</option>
            <option value="role" ${searchBy === 'role' ? 'selected' : ''}>role</option>
          </select>
          <input type="text" class="search-input" placeholder="Search users by ${searchBy}..." value="${esc(searchQuery)}" data-action="search" />
        </div>
        ` : activePanel !== 'dashboard' && activePanel !== 'users' ? `
        <div class="admin-search-bar">
          <span class="search-prompt">$</span>
          <input type="text" class="search-input" placeholder="Filter ${activePanel}..." value="${esc(searchQuery)}" data-action="search" />
        </div>
        ` : ''}
        ${error ? `<div class="admin-error">! ERROR: ${esc(error)}</div>` : ''}
        ${loading ? `<div class="admin-loading">▸ Loading...</div>` : ''}
        ${activePanel !== 'dashboard' ? `
        <div class="admin-list">
          ${items.length === 0 && !loading ? `<div class="admin-empty">▸ No ${activePanel} found</div>` : ''}
          ${items.map((item, i) => this._itemHtml(item, i === selectedIndex)).join('')}
        </div>
        ` : ''}
        ${detail ? this._detailHtml(detail) : ''}
        <div class="admin-footer">
          <span class="status-item">▸ ${activePanel === 'dashboard' ? 'Dashboard' : items.length + ' item' + (items.length !== 1 ? 's' : '')}</span>
          <span class="status-item">▸ selected: ${selectedIndex + 1}</span>
          <span class="status-item">▸ [?] help</span>
        </div>
      </div>
    `;
  }

  _dashboardHtml() {
    const { stats, loading } = this.state;
    const s = stats || {};
    return `
      <div class="dashboard-grid">
        <div class="dashboard-card">
          <div class="card-header">▸ USERS</div>
          <div class="card-value">${s.totalUsers || 0}</div>
          <div class="card-meta">
            <span class="meta-item role-landlord">Landlord: ${s.landlords || 0}</span>
            <span class="meta-item role-tenant">Tenant: ${s.tenants || 0}</span>
            <span class="meta-item role-agent">Agent: ${s.agents || 0}</span>
          </div>
        </div>
        <div class="dashboard-card">
          <div class="card-header">▸ PROPERTIES</div>
          <div class="card-value">${s.totalProperties || 0}</div>
          <div class="card-meta">
            <span class="meta-item">Units: ${s.totalUnits || 0}</span>
          </div>
        </div>
        <div class="dashboard-card">
          <div class="card-header">▸ CONTRACTS</div>
          <div class="card-value">${s.totalContracts || 0}</div>
          <div class="card-meta">
            <span class="meta-item status-active">Active: ${s.activeContracts || 0}</span>
            <span class="meta-item status-pending">Pending: ${s.pendingContracts || 0}</span>
          </div>
        </div>
        <div class="dashboard-card">
          <div class="card-header">▸ INQUIRIES</div>
          <div class="card-value">${s.totalInquiries || 0}</div>
          <div class="card-meta">
            <span class="meta-item status-new">New: ${s.newInquiries || 0}</span>
            <span class="meta-item status-answered">Answered: ${s.answeredInquiries || 0}</span>
          </div>
        </div>
      </div>
      <div class="dashboard-chart">
        <div class="chart-header">▸ RECENT ACTIVITY</div>
        <div class="chart-bar">
          ${this._renderChartBar(s.recentUsers || [])}
        </div>
        <div class="chart-legend">
          <span class="legend-item">Last 7 days user registrations</span>
        </div>
      </div>
      ${loading ? '<div class="admin-loading">▸ Loading stats...</div>' : ''}
    `;
  }

  _renderChartBar(recentUsers) {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const data = Array.isArray(recentUsers) ? recentUsers : [12, 19, 15, 25, 22, 18, 10];
    const max = Math.max(...data, 1);
    return days.map((day, i) => {
      const val = data[i] || 0;
      const height = Math.max(5, (val / max) * 100);
      return `
        <div class="chart-column">
          <div class="chart-bar-fill" style="height: ${height}%"></div>
          <div class="chart-label">${day}</div>
          <div class="chart-value">${val}</div>
        </div>
      `;
    }).join('');
  }

  _itemHtml(item, selected) {
    return `
      <div class="admin-item ${selected ? 'selected' : ''}">
        ${this._formatItem(item)}
      </div>
    `;
  }

  _formatItem(item) {
    if (this.state.activePanel === 'users') {
      const roleColors = { LANDLORD: 'role-landlord', TENANT: 'role-tenant', AGENT: 'role-agent', ADMIN: 'role-admin' };
      return `<span class="item-id">${esc(item.id)}</span><span class="item-name">${esc(item.email || '—')}</span><span class="item-role ${roleColors[item.role] || ''}">[${esc(item.role || 'USER')}]</span><span class="item-fullname">${esc(item.full_name || item.name || '')}</span>`;
    }
    if (this.state.activePanel === 'properties') {
      return `<span class="item-id">${esc(item.id)}</span><span class="item-name">${esc(item.name || item.address_line1 || 'Unnamed')}</span><span class="item-meta">${esc(item.city || '')} ${item.unit_count ? `(${item.unit_count} units)` : ''}</span>`;
    }
    if (this.state.activePanel === 'contracts') {
      return `<span class="item-id">${esc(item.id)}</span><span class="item-name">${esc(item.property_name || 'Property')}</span><span class="item-meta">${esc(item.tenant_name || '—')}</span><span class="item-status">[${esc(item.status || 'PENDING')}]</span>`;
    }
    if (this.state.activePanel === 'inquiries') {
      return `<span class="item-id">${esc(item.id)}</span><span class="item-name">${esc(item.subject || 'Inquiry')}</span><span class="item-sender">${esc(item.sender_email || '—')}</span><span class="item-status">[${esc(item.status || 'NEW')}]</span>`;
    }
    return `<span class="item-id">${esc(item.id)}</span>`;
  }

  _detailHtml(item) {
    return `
      <div class="admin-detail">
        <div class="detail-header">
          <span>▸ DETAIL // ${esc(item.id || 'N/A')}</span>
          <span class="detail-close" data-action="close">[ESC] close</span>
        </div>
        <div class="detail-content">
          ${Object.entries(item).map(([k, v]) => `<div class="detail-row"><span class="detail-key">${esc(k)}:</span><span class="detail-value">${esc(v)}</span></div>`).join('')}
        </div>
        <div class="detail-actions">
          <button class="btn-action" data-action="view">[ENTER] view</button>
          <button class="btn-action btn-danger" data-action="delete">[DEL] delete</button>
        </div>
      </div>
    `;
  }

  mount() {
    document.addEventListener('keydown', this._keyHandler);
    this.el.addEventListener('click', (e) => {
      const panel = e.target.closest('[data-panel]');
      if (panel) this.setState({ activePanel: panel.dataset.panel, selectedIndex: 0, searchQuery: '' }, () => this.load());
    });
    this.el.querySelector('[data-action="search-by"]')?.addEventListener('change', (e) => {
      this.setState({ searchBy: e.target.value, searchQuery: '' }, () => this.load());
    });
    this.el.querySelector('[data-action="search"]')?.addEventListener('input', (e) => {
      this.setState({ searchQuery: e.target.value });
    });
    this.el.querySelector('[data-action="close"]')?.addEventListener('click', () => {
      this.setState({ detail: null });
    });
    this.load();
  }

  unmount() {
    document.removeEventListener('keydown', this._keyHandler);
  }

  _handleKey(e) {
    const { activePanel, items, selectedIndex, detail } = this.state;

    if (e.key === 'Escape') {
      if (detail) this.setState({ detail: null });
      else this.setState({ searchQuery: '' });
      return;
    }

    if (e.key === '/') {
      e.preventDefault();
      this.el.querySelector('.search-input')?.focus();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (detail) return;
      this.setState({ selectedIndex: Math.min(selectedIndex + 1, items.length - 1) });
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (detail) return;
      this.setState({ selectedIndex: Math.max(selectedIndex - 1, 0) });
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (activePanel !== 'dashboard' && items[selectedIndex]) {
        this.setState({ detail: items[selectedIndex] });
      }
      return;
    }

    if (e.key >= '1' && e.key <= '5') {
      const idx = parseInt(e.key) - 1;
      const panel = this.state.panels[idx];
      if (panel) this.setState({ activePanel: panel, selectedIndex: 0, searchQuery: '' }, () => this.load());
      return;
    }
  }

  async load() {
    const { activePanel, searchQuery, searchBy } = this.state;
    this.setState({ loading: true, error: null });

    try {
      if (activePanel === 'dashboard') {
        const res = await api.get('/admin/dashboard');
        
        this.setState({
          stats: {
            totalProperties: res.total_properties || 0,
            totalUnits: res.total_units || 0,
            totalContracts: res.total_contracts || 0,
            activeContracts: res.active_contracts || 0,
            pendingContracts: res.pending_contracts || 0,
            totalInquiries: res.total_inquiries || 0,
            newInquiries: res.new_inquiries || 0,
            answeredInquiries: res.answered_inquiries || 0,
          },
          loading: false,
        });
        return;
      }
      
      let endpoint;
      if (activePanel === 'users') endpoint = '/users';
      else if (activePanel === 'properties') endpoint = '/admin/properties';
      else if (activePanel === 'contracts') endpoint = '/admin/contracts';
      else if (activePanel === 'inquiries') endpoint = '/admin/inquiries';
      else endpoint = `/${activePanel}`;
      
      const res = await api.get(endpoint);
      let data = res.data || [];
      
      if (activePanel === 'users' && searchQuery) {
        const q = searchQuery.toLowerCase();
        data = data.filter(item => {
          if (searchBy === 'all') {
            return (item.email || '').toLowerCase().includes(q) ||
                   (item.full_name || '').toLowerCase().includes(q) ||
                   (item.name || '').toLowerCase().includes(q) ||
                   (item.role || '').toLowerCase().includes(q);
          }
          if (searchBy === 'email') return (item.email || '').toLowerCase().includes(q);
          if (searchBy === 'name') return (item.full_name || '').toLowerCase().includes(q) || (item.name || '').toLowerCase().includes(q);
          if (searchBy === 'role') return (item.role || '').toLowerCase().includes(q);
          return true;
        });
      } else if (searchQuery) {
        const q = searchQuery.toLowerCase();
        data = data.filter(item => JSON.stringify(item).toLowerCase().includes(q));
      }
      
      this.setState({ items: data, selectedIndex: 0, loading: false });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : 'Failed to load', loading: false, items: [] });
    }
  }
}